import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Stack } from "@mui/material";

import Loading from "components/Loading";
import ImportFromDocumentsDialog from "components/dashboard/healthRecords/ImportFromDocumentsDialog";
import { useAuth } from "hooks/useAuth";
import { getProvider } from "config/ehr";
import { completeAuthorization } from "services/ehr/smartAuth";
import { fetchRecord } from "services/ehr/fhirRecordImport";
import {
  buildProposals,
  saveApprovedProposals,
} from "services/healthRecordImportService";
import { medicationService } from "services/medicationService";
import { db } from "services/firebase";

const RECORDS_PATH = "/dashboard/health-records";

/**
 * Where the hospital sends the patient back after they approve access.
 *
 * The connection is finished here and the record read, then the same review
 * dialog the document import uses decides what is written — a hospital record
 * is still proposed rather than merged, because the patient may have been
 * discharged on medications they have since stopped.
 */
const EhrCallbackPage = () => {
  const { t } = useTranslation("masterchart");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [healthRecords, setHealthRecords] = useState({});
  const [saving, setSaving] = useState(false);
  // An authorization code can only be spent once, so the exchange must not run
  // twice — which it otherwise would under React 18's development StrictMode.
  const started = useRef(false);

  useEffect(() => {
    if (!user?.uid || started.current) return;
    started.current = true;

    (async () => {
      try {
        const session = await completeAuthorization(window.location.search);
        const label = getProvider(session.providerId)?.label || "your hospital";

        const [record, snapshot, medications] = await Promise.all([
          fetchRecord(session, label),
          db.collection("users").doc(user.uid).get(),
          medicationService.getMedications(user.uid),
        ]);
        const current = snapshot.data()?.healthRecords || {};

        setHealthRecords(current);
        setExtraction(record);
        setProposals(buildProposals(record, current, medications));
      } catch (err) {
        setError(err?.message || t("ehrCallback.connectionFailed"));
      }
    })();
  }, [user, t]);

  const handleApply = useCallback(
    async (approved) => {
      setSaving(true);
      try {
        await saveApprovedProposals({ userId: user.uid, approved, healthRecords });
        navigate(RECORDS_PATH, { replace: true });
      } catch (err) {
        setError(err?.message || t("common.couldNotSaveRecord"));
      } finally {
        setSaving(false);
      }
    },
    [user, healthRecords, navigate, t]
  );

  if (error) {
    return (
      <Box sx={{ maxWidth: 520, mx: "auto", p: 3 }}>
        <Stack spacing={2}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={() => navigate(RECORDS_PATH, { replace: true })}>
            {t("ehrCallback.backToRecord")}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!extraction) {
    return <Loading page />;
  }

  return (
    <ImportFromDocumentsDialog
      open
      onClose={() => navigate(RECORDS_PATH, { replace: true })}
      loading={false}
      error={null}
      extraction={extraction}
      proposals={proposals}
      saving={saving}
      onApply={handleApply}
    />
  );
};

export default EhrCallbackPage;
